// Feature: features/nma-payer-response-pipeline.md, features/nma-payer-data-analysis-jobs.md, features/nma-claims-beta-banner-and-blurred-summaries.md, features/nma-claims-performance-and-component-extraction.md
import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Select,
  Input,
  Textarea,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  TableContainer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  HStack,
  VStack,
  Divider,
} from '@chakra-ui/react';
import {
  formatCurrency,
  formatLabel,
  getStatusColorScheme,
  getUrgencyColorScheme,
  getCategoryColorScheme,
  normalizeAnalysisText,
  FormattedAnalysisText,
} from './shared';

export const NMAClaimDetailModal = React.memo(({
  isOpen,
  onClose,
  selectedClaimRow,
  claimDetailData,
  detailLoading,
  isAnalyzing,
  onTriggerAnalyze,
}) => {
  const activeClaimBlock = useMemo(() => {
    if (!claimDetailData) return null;
    if (claimDetailData.claim_rollup) return claimDetailData.claim_rollup;
    const byNum = claimDetailData.payer_response_rollup?.claims_by_number || {};
    if (selectedClaimRow?.claim_number && byNum[selectedClaimRow.claim_number]) {
      return byNum[selectedClaimRow.claim_number];
    }
    const firstKey = Object.keys(byNum)[0];
    return firstKey ? byNum[firstKey] : null;
  }, [claimDetailData, selectedClaimRow]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="gray.800" color="white" borderTopRadius="md">
          <Flex justify="space-between" align="center" pr={8}>
            <HStack spacing={3}>
              <Badge colorScheme={selectedClaimRow?.claim_type === 'tech' ? 'purple' : 'blue'} fontSize="sm" px={2} py={0.5}>
                {selectedClaimRow?.claim_type?.toUpperCase()} CLAIM
              </Badge>
              <Text fontSize="lg" fontWeight="bold">
                {selectedClaimRow?.claim_number} (Case #{selectedClaimRow?.nma_case_no})
              </Text>
              {activeClaimBlock && (
                <Badge colorScheme={getStatusColorScheme(activeClaimBlock.current_lifecycle_status)} fontSize="xs">
                  {formatLabel(activeClaimBlock.current_lifecycle_status)}
                </Badge>
              )}
            </HStack>
            {selectedClaimRow && onTriggerAnalyze && (
              <Button
                size="xs"
                colorScheme="blue"
                isLoading={isAnalyzing}
                onClick={() => onTriggerAnalyze(selectedClaimRow.claim_number, true)}
              >
                Re-Synthesize Claim
              </Button>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton color="white" />

        <ModalBody py={5} bg="gray.50">
          {detailLoading ? (
            <Flex justify="center" py={12}>
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : !activeClaimBlock ? (
            <Alert status="warning">
              <AlertIcon />
              Could not load claim details.
            </Alert>
          ) : (
            <VStack spacing={5} align="stretch">
              {/* Recommended Next Action Banner */}
              {activeClaimBlock.recommended_next_action && (
                <Alert
                  status={activeClaimBlock.recommended_next_action.urgency === 'HIGH' ? 'error' : 'info'}
                  variant="left-accent"
                  borderRadius="md"
                  bg="white"
                  shadow="xs"
                >
                  <AlertIcon />
                  <Box flex="1">
                    <HStack spacing={2} mb={0.5}>
                      <Badge colorScheme={getUrgencyColorScheme(activeClaimBlock.recommended_next_action.urgency)}>
                        {activeClaimBlock.recommended_next_action.urgency} URGENCY
                      </Badge>
                      <Text fontWeight="bold" fontSize="sm">
                        {formatLabel(activeClaimBlock.recommended_next_action.action)}
                      </Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.700">
                      {activeClaimBlock.recommended_next_action.details}
                    </Text>
                  </Box>
                </Alert>
              )}

              {/* Claim Metadata & Financial Summary */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Card size="sm" variant="outline" bg="white">
                  <CardHeader pb={1}>
                    <Heading size="xs" color="gray.600" textTransform="uppercase">
                      Submitted Claim (837P / CMS-1500) &amp; Clearinghouse
                    </Heading>
                  </CardHeader>
                  <CardBody fontSize="xs">
                    <SimpleGrid columns={2} spacing={2}>
                      <Box>
                        <Text color="gray.500">Patient:</Text>
                        <Text fontWeight="bold">{claimDetailData?.payer_response_rollup?.patient_name || 'N/A'}</Text>
                      </Box>
                      <Box>
                        <Text color="gray.500">Payer:</Text>
                        <Text fontWeight="bold">{activeClaimBlock.payer_name || selectedClaimRow?.payer_name}</Text>
                      </Box>
                      <Box>
                        <Text color="gray.500">Date / Place of Service:</Text>
                        <Text fontWeight="semibold">
                          {activeClaimBlock.date_of_service || 'N/A'} (POS: {activeClaimBlock.place_of_service || 'N/A'})
                        </Text>
                      </Box>
                      <Box>
                        <Text color="gray.500">Payer ICN / Control #:</Text>
                        <Text fontWeight="semibold">{activeClaimBlock.payer_claim_control_number || 'N/A'}</Text>
                      </Box>
                      <Box>
                        <Text color="gray.500">Facility:</Text>
                        <Text fontWeight="semibold">
                          {activeClaimBlock.submitted_claim?.facility?.name || 'N/A'}
                        </Text>
                      </Box>
                      <Box>
                        <Text color="gray.500">Rendering Provider:</Text>
                        <Text fontWeight="semibold">
                          {activeClaimBlock.submitted_claim?.rendering_provider?.name || 'N/A'}
                        </Text>
                      </Box>
                    </SimpleGrid>
                    {activeClaimBlock.submitted_claim?.icd10_codes?.length > 0 && (
                      <Box mt={3}>
                        <Text color="gray.500" mb={1}>Submitted Box 21 ICD-10 Diagnoses:</Text>
                        <Flex gap={1.5} wrap="wrap">
                          {activeClaimBlock.submitted_claim.icd10_codes.map((dx) => (
                            <Badge key={dx.pointer || dx.code} colorScheme="blue" variant="subtle">
                              {dx.pointer ? `${dx.pointer}: ` : ''}{dx.code}
                            </Badge>
                          ))}
                        </Flex>
                      </Box>
                    )}
                  </CardBody>
                </Card>

                <Card size="sm" variant="outline" bg="white">
                  <CardHeader pb={1}>
                    <Heading size="xs" color="gray.600" textTransform="uppercase">
                      Reconciled Financial Summary (beta)
                    </Heading>
                  </CardHeader>
                  <CardBody>
                    <SimpleGrid columns={3} spacing={3}>
                      <Box p={2} bg="gray.50" borderRadius="md">
                        <Text fontSize="xs" color="gray.500">Total Billed</Text>
                        <Text fontSize="md" fontWeight="bold">
                          {formatCurrency(activeClaimBlock.financial_summary?.total_billed)}
                        </Text>
                      </Box>
                      <Box p={2} bg="gray.50" borderRadius="md">
                        <Text fontSize="xs" color="gray.500">Total Allowed</Text>
                        <Text fontSize="md" fontWeight="bold" color="blue.600">
                          {formatCurrency(activeClaimBlock.financial_summary?.total_allowed)}
                        </Text>
                      </Box>
                      <Box p={2} bg="green.50" borderRadius="md">
                        <Text fontSize="xs" color="green.700">Total Paid</Text>
                        <Text fontSize="md" fontWeight="bold" color="green.700">
                          {formatCurrency(activeClaimBlock.financial_summary?.total_paid)}
                        </Text>
                      </Box>
                      <Box p={2} bg="red.50" borderRadius="md">
                        <Text fontSize="xs" color="red.700">Total Denied</Text>
                        <Text fontSize="md" fontWeight="bold" color="red.600">
                          {formatCurrency(activeClaimBlock.financial_summary?.total_denied)}
                        </Text>
                      </Box>
                      <Box p={2} bg="teal.50" borderRadius="md">
                        <Text fontSize="xs" color="teal.700">Patient Resp (PR)</Text>
                        <Text fontSize="md" fontWeight="bold" color="teal.700">
                          {formatCurrency(activeClaimBlock.financial_summary?.total_patient_responsibility)}
                        </Text>
                      </Box>
                      <Box p={2} bg="gray.50" borderRadius="md">
                        <Text fontSize="xs" color="gray.500">Contractual Adj (CO)</Text>
                        <Text fontSize="md" fontWeight="bold" color="gray.700">
                          {formatCurrency(activeClaimBlock.financial_summary?.total_contractual_adjustment)}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {/* Line-Item Adjudication Table */}
              <Card size="sm" variant="outline" bg="white">
                <CardHeader pb={2}>
                  <Heading size="xs" color="gray.700" textTransform="uppercase">
                    Reconciled Line-Item Adjudication ({activeClaimBlock.active_line_items?.length || 0} CPT Lines)
                  </Heading>
                </CardHeader>
                <CardBody pt={0}>
                  <TableContainer>
                    <Table size="sm">
                      <Thead bg="gray.100">
                        <Tr>
                          <Th>CPT</Th>
                          <Th>Mods</Th>
                          <Th>Ptrs</Th>
                          <Th isNumeric>Units</Th>
                          <Th isNumeric>Billed (beta)</Th>
                          <Th isNumeric>Allowed (beta)</Th>
                          <Th isNumeric>Paid (beta)</Th>
                          <Th isNumeric>Denied (beta)</Th>
                          <Th isNumeric>PR (beta)</Th>
                          <Th>Category</Th>
                          <Th>CARC / RARC</Th>
                          <Th>Payer Explanation / Glossary</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(activeClaimBlock.active_line_items || []).map((li, idx) => (
                          <Tr key={`${li.cpt_code}-${idx}`}>
                            <Td fontWeight="bold">
                              {li.cpt_code}
                              {li.unlisted_description && (
                                <Text fontSize="10px" color="gray.500">{li.unlisted_description}</Text>
                              )}
                            </Td>
                            <Td>{(li.modifiers || []).join(', ') || '-'}</Td>
                            <Td>{(li.diag_pointers || []).join(', ') || '-'}</Td>
                            <Td isNumeric>{li.units}</Td>
                            <Td isNumeric>{formatCurrency(li.billed_amount)}</Td>
                            <Td isNumeric>{formatCurrency(li.allowed_amount)}</Td>
                            <Td isNumeric fontWeight="bold" color={li.paid_amount > 0 ? 'green.600' : 'gray.500'}>
                              {formatCurrency(li.paid_amount)}
                            </Td>
                            <Td isNumeric fontWeight="bold" color={li.denied_amount > 0 ? 'red.600' : 'gray.400'}>
                              {formatCurrency(li.denied_amount)}
                            </Td>
                            <Td isNumeric color={li.patient_responsibility > 0 ? 'teal.600' : 'gray.400'}>
                              {formatCurrency(li.patient_responsibility)}
                            </Td>
                            <Td>
                              <Badge colorScheme={getCategoryColorScheme(li.denial_category)} fontSize="10px">
                                {formatLabel(li.denial_category)}
                              </Badge>
                            </Td>
                            <Td>
                              <HStack spacing={1}>
                                {li.carc && <Badge colorScheme="red" variant="outline" fontSize="10px">{li.carc}</Badge>}
                                {li.rarc && <Badge colorScheme="purple" variant="outline" fontSize="10px">{li.rarc}</Badge>}
                                {li.footnote_code && (
                                  <Badge colorScheme="orange" variant="subtle" fontSize="10px">
                                    Fn: {li.footnote_code}
                                  </Badge>
                                )}
                              </HStack>
                            </Td>
                            <Td maxW="320px">
                              <Text fontSize="xs" color="gray.700" whiteSpace="normal">
                                {li.description || '-'}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>

              {/* Chronological Lifecycle Timeline */}
              <Card size="sm" variant="outline" bg="white">
                <CardHeader pb={2}>
                  <Heading size="xs" color="gray.700" textTransform="uppercase">
                    Chronological Claim Lifecycle Timeline ({activeClaimBlock.chronological_timeline?.length || 0} Events)
                  </Heading>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={2} divider={<Divider />}>
                    {(activeClaimBlock.chronological_timeline || []).map((ev, i) => (
                      <Flex key={`${ev.extraction_id || i}`} justify="space-between" align="flex-start" gap={4} py={1}>
                        <HStack spacing={2} minW="240px">
                          <Badge colorScheme="blue" variant="subtle" fontSize="10px">
                            {ev.document_date || ev.timestamp?.slice(0, 10) || 'N/A'}
                          </Badge>
                          <Badge colorScheme="purple" fontSize="10px">
                            {ev.zinniax_tag_name || ev.document_subtype}
                          </Badge>
                        </HStack>
                        <Text flex="1" fontSize="xs" color="gray.800">
                          {ev.summary}
                        </Text>
                        {ev.timestamp && (
                          <Text fontSize="10px" color="gray.400" minW="135px" textAlign="right">
                            ZinniaX: {ev.timestamp.replace('T', ' ').slice(0, 16)}
                          </Text>
                        )}
                      </Flex>
                    ))}
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter bg="white" borderBottomRadius="md">
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

export const NMAAnalysisReportModal = React.memo(({
  isOpen,
  onClose,
  selectedAnalysisItem,
  isGeneratingJobs,
  onReRunAnalysisJob,
}) => (
  <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
    <ModalOverlay />
    <ModalContent>
      <ModalHeader bg="purple.800" color="white" borderTopRadius="md">
        <Flex justify="space-between" align="center" pr={8} wrap="wrap" gap={2}>
          <HStack spacing={2}>
            <Badge colorScheme="purple" variant="solid" fontSize="xs">
              {formatLabel(selectedAnalysisItem?.content_type || 'PAYER_DATA_ANALYSIS')}
            </Badge>
            <Text fontSize="md" fontWeight="bold">
              {normalizeAnalysisText(
                selectedAnalysisItem?.payer_data_analysis?.analysis_result?.headline ||
                  `${selectedAnalysisItem?.payer_name || ''} ${selectedAnalysisItem?.cpt_code ? `• CPT ${selectedAnalysisItem.cpt_code}` : ''}`
              )}
            </Text>
          </HStack>
          {selectedAnalysisItem && onReRunAnalysisJob && (
            <Button
              size="xs"
              colorScheme="purple"
              variant="outline"
              color="white"
              _hover={{ bg: 'purple.700' }}
              isLoading={isGeneratingJobs}
              onClick={() =>
                onReRunAnalysisJob({
                  target_item: {
                    content_type: selectedAnalysisItem.content_type || 'payer_hotspot',
                    target_key: selectedAnalysisItem.target_key,
                    entity_refs:
                      selectedAnalysisItem.entity_refs ||
                      selectedAnalysisItem.payer_data_analysis?.entity_refs ||
                      {},
                    source_snapshot:
                      selectedAnalysisItem.source_snapshot ||
                      selectedAnalysisItem.payer_data_analysis?.source_snapshot ||
                      selectedAnalysisItem,
                  },
                  force: true,
                })
              }
            >
              Re-Run Analysis Job
            </Button>
          )}
        </Flex>
      </ModalHeader>
      <ModalCloseButton color="white" />

      <ModalBody py={5} bg="gray.50">
        {selectedAnalysisItem?.payer_data_analysis ? (
          (() => {
            const pda = selectedAnalysisItem.payer_data_analysis;
            const ar = pda.analysis_result || {};
            const metrics = ar.metrics || {};
            const oppDollars =
              pda.opportunity_size_dollars ||
              ar.opportunity_size_dollars ||
              metrics.opportunity_size_dollars ||
              0;
            const recActions = ar.recommended_actions || [];
            const claimRefs = pda.entity_refs?.claim_numbers || selectedAnalysisItem.claim_numbers || [];

            return (
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3}>
                  <Card size="sm" variant="outline" bg="white" borderTop="3px solid" borderTopColor="green.500">
                    <CardBody>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold">SIZE OF OPPORTUNITY (beta)</Text>
                      <Text fontSize="xl" fontWeight="extrabold" color="green.600">
                        {formatCurrency(oppDollars)}
                      </Text>
                      {metrics.recoverable_ar_dollars > 0 && (
                        <Text fontSize="11px" color="gray.600">
                          Recoverable A/R (beta): {formatCurrency(metrics.recoverable_ar_dollars)}
                        </Text>
                      )}
                    </CardBody>
                  </Card>
                  <Card size="sm" variant="outline" bg="white" borderTop="3px solid" borderTopColor="purple.500">
                    <CardBody>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold">AFFECTED CLAIMS SCOPE</Text>
                      <Text fontSize="xl" fontWeight="extrabold" color="purple.700">
                        {metrics.affected_claims_count || claimRefs.length || 0} Claims
                      </Text>
                      <Text fontSize="11px" color="gray.600" noOfLines={1}>
                        Target: {pda.target_key}
                      </Text>
                    </CardBody>
                  </Card>
                  <Card size="sm" variant="outline" bg="white" borderTop="3px solid" borderTopColor="blue.500">
                    <CardBody>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold">CONFIDENCE &amp; PRIORITY</Text>
                      <HStack spacing={2} mt={1}>
                        <Badge colorScheme="blue">{metrics.confidence || 'HIGH'} CONFIDENCE</Badge>
                        <Badge colorScheme="red">{metrics.priority || 'HIGH'} PRIORITY</Badge>
                      </HStack>
                      {pda.updated_at && (
                        <Text fontSize="11px" color="gray.500" mt={1}>
                          Updated: {pda.updated_at.replace('T', ' ').slice(0, 16)} UTC
                        </Text>
                      )}
                    </CardBody>
                  </Card>
                </SimpleGrid>

                {ar.research_description && (
                  <Card size="sm" variant="outline" bg="white" borderLeft="4px solid" borderLeftColor="blue.500">
                    <CardHeader pb={1}>
                      <Heading size="xs" color="blue.800" textTransform="uppercase">
                        Research Description
                      </Heading>
                    </CardHeader>
                    <CardBody pt={1}>
                      <FormattedAnalysisText
                        text={ar.research_description}
                        fontSize="sm"
                        color="gray.800"
                        mt={0}
                      />
                    </CardBody>
                  </Card>
                )}

                {(ar.executive_summary || ar.root_cause_summary) && (
                  <Card size="sm" variant="outline" bg="white">
                    <CardHeader pb={1}>
                      <Heading size="xs" color="purple.800" textTransform="uppercase">
                        Executive Summary
                      </Heading>
                    </CardHeader>
                    <CardBody pt={1}>
                      <FormattedAnalysisText
                        text={ar.executive_summary || ar.root_cause_summary}
                        fontSize="sm"
                        color="gray.800"
                        mt={0}
                      />
                    </CardBody>
                  </Card>
                )}

                {(ar.recommendation || recActions.length > 0) && (
                  <Card size="sm" variant="outline" bg="white">
                    <CardHeader pb={1}>
                      <Heading size="xs" color="green.800" textTransform="uppercase">
                        Recommendations: Rules &amp; Processes
                      </Heading>
                    </CardHeader>
                    <CardBody pt={1}>
                      {ar.recommendation && (
                        <FormattedAnalysisText
                          text={ar.recommendation}
                          fontSize="sm"
                          color="gray.800"
                          mt={0}
                          mb={recActions.length ? 3 : 0}
                        />
                      )}
                      {recActions.length > 0 && (
                        <VStack align="stretch" spacing={2}>
                          {recActions.map((act, i) => (
                            <Box key={i} p={2.5} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                              <HStack spacing={2} mb={0.5}>
                                {act.action_type && (
                                  <Badge colorScheme="green" fontSize="10px">
                                    {act.action_type}
                                  </Badge>
                                )}
                                <Text fontSize="xs" fontWeight="bold" color="gray.800">
                                  {normalizeAnalysisText(act.title)}
                                </Text>
                              </HStack>
                              {act.details && (
                                <FormattedAnalysisText
                                  text={act.details}
                                  fontSize="xs"
                                  color="gray.700"
                                  mt={0.5}
                                />
                              )}
                            </Box>
                          ))}
                        </VStack>
                      )}
                    </CardBody>
                  </Card>
                )}

                {(ar.evidence_markdown || ar.report_markdown) && (
                  <Card size="sm" variant="outline" bg="white">
                    <CardHeader pb={1}>
                      <Heading size="xs" color="gray.700" textTransform="uppercase">
                        Detailed &amp; Comprehensive Evidence
                      </Heading>
                    </CardHeader>
                    <CardBody pt={1}>
                      <Box
                        className="markdown-content"
                        fontSize="sm"
                        color="gray.800"
                        sx={{
                          'h1, h2, h3': { fontWeight: 'bold', mt: 3, mb: 1.5 },
                          p: { mb: 2, whiteSpace: 'pre-wrap' },
                          ul: { listStyleType: 'disc', pl: 5, mb: 2 },
                          ol: { listStyleType: 'decimal', pl: 5, mb: 2 },
                          li: { mb: 1.5, whiteSpace: 'pre-wrap' },
                          table: { width: '100%', borderCollapse: 'collapse', my: 3, fontSize: 'xs' },
                          'th, td': { border: '1px solid #E2E8F0', p: 1.5, textAlign: 'left' },
                          th: { bg: 'gray.100', fontWeight: 'bold' },
                          code: { bg: 'gray.100', px: 1, borderRadius: 'sm', fontSize: 'xs' },
                        }}
                      >
                        <ReactMarkdown>{normalizeAnalysisText(ar.evidence_markdown || ar.report_markdown)}</ReactMarkdown>
                      </Box>
                    </CardBody>
                  </Card>
                )}
              </VStack>
            );
          })()
        ) : (
          <Alert status="info">
            <AlertIcon />
            No completed PayerDataAnalysis report is attached to this item yet. Click &quot;Re-Run Analysis Job&quot; above to queue it.
          </Alert>
        )}
      </ModalBody>

      <ModalFooter bg="white" borderBottomRadius="md">
        <Button size="sm" onClick={onClose}>Close</Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
));

const CLICKABLE_RESEARCH_EXAMPLES = [
  'What are common CPT code denials and reasons across BCBS plans?',
  'What are the most actionable denial trends and rule opportunities across payers?',
  'What issues are we seeing with NPI, EDI Payer IDs, or 277CA clearinghouse rejections?',
];

const SPECIFIC_RESEARCH_IDEAS = [
  'Should we convert 95941 to G0453 for BCBS Texas?',
  'What are new actionable trends from CIGNA?',
  'Should we strip repeat baseline modifier -76 for a specific payer?',
];

// Self-contained form state inside NMACustomResearchModal prevents dashboard re-renders on keystroke
// (see features/nma-claims-performance-and-component-extraction.md)
export const NMACustomResearchModal = React.memo(({
  isOpen,
  onClose,
  isGeneratingJobs,
  onSubmitCustomTask,
}) => {
  const toast = useToast();
  const [customTaskPrompt, setCustomTaskPrompt] = useState('');

  const handleSubmit = async () => {
    const trimmedPrompt = customTaskPrompt.trim();
    if (!trimmedPrompt) {
      toast({
        title: 'Missing Research Ask',
        description: 'Please enter a research question or topic to investigate.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const derivedTitle =
      trimmedPrompt.length > 80 ? `${trimmedPrompt.slice(0, 77)}...` : trimmedPrompt;
    const ok = await onSubmitCustomTask({
      title: derivedTitle,
      prompt: trimmedPrompt,
      payer_name: '',
      cpt_code: '',
    });
    if (ok !== false) {
      setCustomTaskPrompt('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="teal.700" color="white" borderTopRadius="md">
          Create Payer Data Research Task
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody py={4}>
          <VStack spacing={3} align="stretch">
            <Text fontSize="xs" color="gray.600">
              Ask any question about NMA claims, payer behavior, coding rules, or clearinghouse/EOB trends. Our analysis agent will query the database and publish a structured report with recommendations and evidence.
            </Text>
            <Box bg="gray.50" p={3} borderRadius="md" borderWidth="1px" borderColor="gray.200">
              <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1.5}>
                Quick-Start Research Topics (click to populate):
              </Text>
              <VStack align="stretch" spacing={1.5}>
                {CLICKABLE_RESEARCH_EXAMPLES.map((example) => (
                  <Box
                    key={example}
                    as="button"
                    type="button"
                    textAlign="left"
                    fontSize="xs"
                    color="teal.700"
                    px={2}
                    py={1}
                    borderRadius="sm"
                    bg="white"
                    borderWidth="1px"
                    borderColor="teal.100"
                    _hover={{ bg: 'teal.50', borderColor: 'teal.300' }}
                    onClick={() => setCustomTaskPrompt(example)}
                  >
                    &ldquo;{example}&rdquo;
                  </Box>
                ))}
              </VStack>
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={0.5}>
                What would you like to investigate?
              </Text>
              <Text fontSize="11px" color="gray.500" mb={1.5}>
                Or type a specific question such as:{' '}
                {SPECIFIC_RESEARCH_IDEAS.map((idea, idx) => (
                  <React.Fragment key={idea}>
                    <i>&ldquo;{idea}&rdquo;</i>
                    {idx < SPECIFIC_RESEARCH_IDEAS.length - 1 ? ' • ' : ''}
                  </React.Fragment>
                ))}
              </Text>
              <Textarea
                size="sm"
                rows={4}
                placeholder={'Type your research question here...'}
                value={customTaskPrompt}
                onChange={(e) => setCustomTaskPrompt(e.target.value)}
              />
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2}>
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              colorScheme="teal"
              isLoading={isGeneratingJobs}
              onClick={handleSubmit}
            >
              Queue Research Job
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
